import type { EngineInput, OverrideIn, ValidationMessage } from '@conciair/scheduler-core';
import type { OverrideKind, OverrideRow, OverrideSource, Seat } from '../../data/database.types';
import { engine } from '../../engine/client';

/**
 * Spec 6.3: an override is validated against the live snapshot, with the
 * new record in place, before it is saved. Shared by the Rules form and the
 * Seat panel so both refuse exactly what the engine would refuse.
 */
export interface OverrideValues {
  kind: OverrideKind;
  pilotCode: string;
  pilotId: string;
  aircraftId: string | null;
  tail: string | null;
  rotationStart: string;
  seat: Seat | null;
  days: number | null;
  notes: string;
}

export interface OverrideCheck {
  errors: ValidationMessage[];
  warnings: ValidationMessage[];
}

/** Run V1 validateInput on the snapshot plus the candidate; return only the candidate's messages. */
export async function checkOverride(input: EngineInput, values: OverrideValues, replacing?: OverrideRow): Promise<OverrideCheck> {
  const others = input.overrides.filter((o) => o._ref !== replacing?.id);
  const candidate: OverrideIn = {
    kind: values.kind,
    pilotId: values.pilotCode,
    tail: values.tail,
    rotationStart: values.rotationStart,
    seat: values.kind === 'LENGTH' || values.kind === 'PIN' ? values.seat : null,
    days: values.kind === 'LENGTH' ? values.days : null,
    notes: values.notes,
    _row: others.length + 1,
  };
  const trial: EngineInput = { ...input, overrides: [...others, candidate] };
  const report = await engine.validate(trial);
  const where = `Overrides row ${others.length + 1}`;
  const mine = (msgs: ValidationMessage[]) => msgs.filter((m) => m.where === where);
  return { errors: mine(report.errors), warnings: mine(report.warnings) };
}

/** Database row values for insert or update. */
export function toRow(values: OverrideValues, source: OverrideSource) {
  return {
    kind: values.kind,
    pilot_id: values.pilotId,
    aircraft_id: values.aircraftId,
    rotation_start: values.rotationStart,
    seat: values.kind === 'LENGTH' || values.kind === 'PIN' ? values.seat : null,
    days: values.kind === 'LENGTH' ? values.days : null,
    notes: values.notes,
    source,
  };
}

/** The exact DOUBLE suggestion the engine embeds in a COVERAGE_GAP explanation (V1 wording). */
export function parseDoubleSuggestions(explanation: string): { pilotCode: string; tail: string; rotationStart: string }[] {
  const out: { pilotCode: string; tail: string; rotationStart: string }[] = [];
  const re = /pilot_id=([^,\s]+), tail_number=([^,\s]+), rotation_start_date=(\d{4}-\d{2}-\d{2})/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(explanation)) !== null) {
    out.push({ pilotCode: m[1]!, tail: m[2]!, rotationStart: m[3]! });
  }
  return out;
}
